"use client";

import { useEffect, useState, useRef } from "react";
import { useParams } from "next/navigation";
import {
  Anchor,
  Container,
  Title,
  Text,
  Paper,
  Stack,
  TextInput,
  Select,
  Button,
  Group,
  ActionIcon,
  Textarea,
  Radio,
  SegmentedControl,
  Switch,
  Divider,
  Alert,
  Modal,
  LoadingOverlay,
  Image,
  ScrollArea,
  Grid,
  Flex,
  InputLabel,
  InputError,
  Center,
  Box,
  NumberInput,
} from "@mantine/core";
import { formRootRule, useForm } from "@mantine/form";
import { DatePickerInput } from "@mantine/dates";
import {
  IconPlus,
  IconTrash,
  IconAlertCircle,
  IconEraser,
} from "@tabler/icons-react";
import { db } from "@/app/_firebase/firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import {
  calculateAddition,
  calculateJob,
  calculateLineItem,
  calculateMultiplication,
} from "@/app/_helpers/math";
import {
  directionOptions,
  mainPoseOptions,
  mesquiteMainPoseOptions,
  whiteOakMainPoseOptions,
  myrickMainPoseOptions,
  safariStudioMainPoseOptions,
  toppinMainPoseOptions,
  bonezMainPoseOptions,
  clWildlifeMainPoseOptions,
  taxidermyDesignsMainPoseOptions,
  peakMainPoseOptions,
  peakEarOptions,
  safariStudioDirectionOptions,
  trueLifeDirectionOptions,
} from "@/app/_constants/poses";
import { Country } from "react-phone-number-input";
import { countryCode, groupedCountryOptions } from "@/app/_constants/countries";
import { usStatesOptions } from "@/app/_constants/unitedStates";
import PhoneInput from "@/app/_components/PhoneInput/PhoneInput";
import { addressFields } from "@/app/_constants/addressFormat";
import { Address } from "@/app/@types/address";
import {
  findOptionValueByText,
  findUsStateByText,
} from "@/app/_helpers/searchableSelect";
import { countryToCurrency } from "@/app/_constants/currency";
import { JobLineItem, JobSpecimenLineItem, JobV2 } from "@/app/@types/job";
import { generateUid } from "@/app/_firebase/general";
import {
  findCountryCodeByName,
  getCurrencyDetailsByCode,
} from "@/app/_helpers/country";
import {
  generateDefaultServiceLineItem,
  generateDefaultSpecimenLineItem,
} from "@/app/_constants/job";
import { NamedUserUID, PaymentStatus } from "@/app/@types/enums";
import { isServiceLineItem, isSpecimenLineItem } from "@/app/_helpers/lineItem";
import {
  complianceDocLabels,
  getComplianceDocType,
  recordsSpecimenSex,
  SPECIMEN_SEX_OPTIONS,
} from "@/app/_helpers/complianceDocument";
import {
  getAdditionalField,
  getPartnerName,
  isPartnerSlugDoc,
  PartnerAdditionalField,
} from "@/app/_helpers/partnerBookingForm";
import SignatureCanvas from "react-signature-canvas";

// Pattons take bookings in two steps: the request form here, then the customer
// picks a drop-off time. Their scheduling page is a Squarespace Scheduling
// (Acuity) block, so we embed that scheduler directly instead of sending them
// off site — same booking, without leaving the confirmation.
const PATTONS_SCHEDULING_URL =
  "https://www.pattonstaxidermyshop.com/scheduling";
const PATTONS_SCHEDULER_EMBED_URL =
  "https://app.squarespacescheduling.com/schedule.php?owner=29682767";

// Which address layout a country uses: the ones we have a format for get their
// own, everything else falls back to the generic one. Mirrors the country
// effect below so validation and rendering agree on the field set.
const getAddressFormatKey = (country?: string | null): string => {
  if (!country) return "United States";
  return groupedCountryOptions[0].items.includes(country) ? country : "other";
};

// Every field name across all address layouts, so one rule per name can be
// registered up front and decide at validation time whether it applies.
const ALL_ADDRESS_FIELD_NAMES = Array.from(
  new Set(
    Object.values(addressFields).flatMap((fields) =>
      fields.map((field) => field.name)
    )
  )
);

interface SlugData {
  uid: string;
  businessName: string;
  logoUrl?: string;
  portalBkgColor: string;
  portalTextColor: string;
  currency: string;
  measurementSystem?: string;
  country: string;
  // Studio's own state; picks which wildlife record the form collects
  state?: string | null;
  taxRate?: number;
  wrdCollection?: boolean;
  hidePricing?: boolean;
  requestAddress?: boolean;
  disclaimerText?: string;
  // Partner booking form (outfitter, ranch, processor). When fixedClientUid is
  // set every booking is billed to that client and the details collected below
  // belong to the end client; otherwise the person on the form is the client.
  partnerForm?: boolean;
  partnerName?: string;
  fixedClientUid?: string;
  fixedClientName?: string;
  partnerLogoUrl?: string;
  jobTag?: string;
  additionalField?: PartnerAdditionalField | null;
  defaultCounty?: string;
  defaultState?: string;
}

export default function BookingPage() {
  const params = useParams();
  const slug = params.slug as string;
  const [showWrdPdf, setShowWrdPdf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [slugData, setSlugData] = useState<SlugData | null>(null);
  const [speciesOptions, setSpeciesOptions] = useState<string[]>([]);

  const [serviceOptions, setServiceOptions] = useState<string[]>([]);
  const [priceList, setPriceList] = useState<any>(null);
  // The business's mount type names in their configured settings order.
  const [mountTypeOrder, setMountTypeOrder] = useState<string[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [selectedPhoneCode, setSelectedPhoneCode] = useState<Country>();
  const [selectedAddressCountry, setSelectedAddressCountry] =
    useState<string>("United States");

  const signatureRef = useRef<SignatureCanvas>(null);
  const [hasSignature, setHasSignature] = useState(false);

  const primaryColor = slugData?.portalBkgColor || "#0099f6";
  const secondaryColor = slugData?.portalTextColor || "#001929";

  const isPattons = slugData?.uid === NamedUserUID.PATTONS;
  // Peak (ticket #3): their own poses plus ear positions, and no county,
  // harvest date or harvest location on the form.
  const isPeak = slugData?.uid === NamedUserUID.PEAK_TAXIDERMY;

  // Booking taken through a partner's QR: the header is co-branded to make
  // that obvious to whoever is filling it in. When the form is pinned to a
  // billing client the contact fields here are the end client's, not the payer's.
  const isPartnerForm = isPartnerSlugDoc(slugData);
  const isBilledToPartner = !!slugData?.fixedClientUid;
  const partnerName = getPartnerName(slugData);
  // One extra required question the partner wants answered on every booking.
  const additionalField = getAdditionalField(slugData);
  // Outfitter forms prefill the WRD's Ranch/Outfitter with the partner. Toppin's
  // partners are processors, not where the animal was taken, so leave it blank.
  const prefillRanchOutfitter =
    isPartnerForm && slugData?.uid !== NamedUserUID.TOPPIN;

  // Possible units a charge-by-unit item could be measured in, derived from the
  // business's measurement system. We can't know the exact unit at booking time,
  // so this just gives the customer a reference for their estimate.
  const possibleUnits =
    slugData?.measurementSystem === "Metric"
      ? slugData?.uid === "U37IPIQ8Iaf9gzPV7A6tO2xSyRo2"
        ? "cm"
        : "m²"
      : "sq ft, linear ft or inches";

  // The address inputs only carry the browser's native required attribute,
  // which typed-but-unpicked text in a searchable Select satisfies. Validate
  // them properly so a booking can't go out with, say, the state blank (the
  // WRD needs the hunter's full address).
  const addressFieldRule =
    (name: string) => (value: string | undefined, values: JobV2) => {
      if (slugData?.requestAddress === false) return null;
      const format = getAddressFormatKey(values.client?.address?.country);
      const field = addressFields[format]?.find((f) => f.name === name);
      if (!field) return null;
      const isRequired =
        field.required !== false && name.toLowerCase() !== "county";
      if (isRequired && !value?.trim()) {
        return `${field.label} is required`;
      }
      return null;
    };

  // Which wildlife record this studio collects. Texas studios sign the WRD;
  // a Maine studio keeps a per-animal record (MOSES ID on the client, seal
  // number on each mount) for the state Taxidermy Report Book.
  const complianceDocType = getComplianceDocType(slugData?.state);
  const complianceLabels = complianceDocLabels[complianceDocType];
  // A long License Number label wraps in a third of the row, so those
  // states get two license fields per row
  const licenseColSpan = complianceLabels.licenseNumberLabel ? 6 : 4;
  const isMaineRecord = complianceDocType === "maine-wildlife-record";
  const collectsMaineRecord = !!slugData?.wrdCollection && isMaineRecord;
  // Taxidermy Designs record each animal's sex on their WRD
  const collectsSpecimenSex =
    !!slugData?.wrdCollection && recordsSpecimenSex(slugData?.uid);

  // Only Top Pin's forms show the "I am the hunter" box for now (ticket #11).
  const showHunterOption =
    !!slugData?.wrdCollection && slugData?.uid === NamedUserUID.TOPPIN;
  // Hookin B only takes a hunting license on the WRD, never a driver's
  // license (ticket #3), so the License Type is locked to hunting.
  const huntingLicenseOnly =
    !!slugData?.wrdCollection && slugData?.uid === NamedUserUID.HOOKINB;
  const hunterFieldsRequired = (values: any) =>
    showHunterOption && values?.extraData?.isHunter === false;
  const hunterAddressRequired = (values: any) =>
    hunterFieldsRequired(values) && !values?.extraData?.hunterSameAddress;

  const form = useForm<JobV2>({
    initialValues: {
      uid: generateUid(),
      jobId: 0,
      staffQuery: [],
      actionsQuery: [],
      history: [],
      payments: [],

      staff: [],
      actions: [],
      client: {
        uid: "",
        firstName: "",
        lastName: "",
        fullName: "",
        currency: slugData?.currency
          ? getCurrencyDetailsByCode(slugData.currency) || {
              code: "USD",
              symbol: "$",
            }
          : { code: "USD", symbol: "$" },
        contactDetails: {
          phone: "",
          email: "",
        },
        address: {
          country: "",
          streetAddress: "",
          city: "",
          state: "",
          zipCode: "",
        },
        syncedToAccountingIntegration: false,
        autoStageEmails: null,
        autoStageSMS: null,
      },
      shipping: "Pickup",
      priority: null,
      tags: ["active"],
      notes: "",
      ids: [],
      files: [],
      lineItems: [generateDefaultSpecimenLineItem()],
      subtotal: 0,
      totalDiscount: 0,
      subtotalAfterDiscounts: 0,
      taxRate: 0,
      includeTax: false,
      totalTax: 0,
      grandTotal: 0,
      totalDeposit: 0,
      remainingBalance: 0,
      paymentStatus: PaymentStatus.AwaitingDeposit,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      completedAt: null,
      syncedToAccountingIntegration: false,
      // @ts-ignore
      extraData: {
        ranchOutfitter: "",
        county: "",
        state: "",
        licenseType: "",
        licenseNumber: "",
        licenseState: "",
        signature: "",
        // Maine: the hunter's MOSES ID, saved against the client on approval
        mosesId: "",
        additionalFieldValue: "",
        // Top Pin trial: the person booking is usually the hunter, but a
        // processor drop-off or a parent booking for a minor is not. The WRD
        // must name the actual hunter, so unticking reveals their details.
        isHunter: true,
        // Hunter lives at the booker's address (parent booking for a minor),
        // so the address fields are skipped and the booker's address is used.
        hunterSameAddress: false,
        hunter: {
          firstName: "",
          lastName: "",
          phone: "",
          address: {
            country: "United States",
            streetAddress: "",
            city: "",
            state: "",
            zipCode: "",
          },
        },
      },
    },
    validate: {
      client: {
        firstName: (value) => (!value ? "First name is required" : null),
        lastName: (value) => (!value ? "Last name is required" : null),
        contactDetails: {
          phone: (value, values) => {
            // Top Pin always requires a phone number, regardless of email
            if (slugData?.uid === NamedUserUID.TOPPIN && !value) {
              return "Phone number is required";
            }
            if (!value && !values.client.contactDetails.email) {
              return "Phone is required when email is not provided";
            }
            return null;
          },
          email: (value, values) => {
            if (!value && !values.client.contactDetails.phone) {
              return "Email is required when phone is not provided";
            }
            if (value && !/^\S+@\S+$/.test(value)) {
              return "Invalid email";
            }
            return null;
          },
        },
        address: Object.fromEntries(
          ALL_ADDRESS_FIELD_NAMES.map((name) => [name, addressFieldRule(name)])
        ),
      },
      lineItems: {
        [formRootRule]: (items: JobLineItem[]) => {
          // Check if there's at least one specimen line item
          const hasSpecimen = items.some(
            (item: JobLineItem) => "specimen" in item && item.specimen
          );
          if (!hasSpecimen) {
            return "At least one mount is required";
          }
          return null;
        },
        // Form-only field, written into the mount notes on submit
        //@ts-ignore
        specimenSex: (value, values, path) => {
          const item = values.lineItems[Number(path.split(".")[1])];
          if (!item || !isSpecimenLineItem(item)) return null;
          if (collectsSpecimenSex && !value) return "Sex is required";
          return null;
        },
        specimen: {
          species: (value, values, path) => {
            const item = values.lineItems[Number(path.split(".")[1])];
            if (!item || !isSpecimenLineItem(item)) return null;
            if (!value) return "Species is required";
            return null;
          },
          // Safari Studios requires mount type, pose and direction on each mount
          mountType: (value, values, path) => {
            const item = values.lineItems[Number(path.split(".")[1])];
            if (!item || !isSpecimenLineItem(item)) return null;
            if (
              slugData?.uid === NamedUserUID.SAFARISTUDIO &&
              item.specimen.species !== "Other" &&
              !value
            ) {
              return "Mount type is required";
            }
            return null;
          },
          mainPose: (value, values, path) => {
            const item = values.lineItems[Number(path.split(".")[1])];
            if (!item || !isSpecimenLineItem(item)) return null;
            if (slugData?.uid === NamedUserUID.SAFARISTUDIO && !value) {
              return "Pose is required";
            }
            return null;
          },
          direction: (value, values, path) => {
            const item = values.lineItems[Number(path.split(".")[1])];
            if (!item || !isSpecimenLineItem(item)) return null;
            if (slugData?.uid === NamedUserUID.SAFARISTUDIO && !value) {
              return "Direction is required";
            }
            return null;
          },
        },
      },
      // @ts-ignore
      extraData: {
        signature: (value, values) => {
          // Only require signature if wrdCollection is enabled
          if (slugData?.wrdCollection && !value) {
            return "Signature is required";
          }
          return null;
        },
        ranchOutfitter: (value) => {
          // Required on most wildlife records; Skins and Scales also require
          // it without one. Peak doesn't ask for it (Jeff, 3 Nov 2026).
          if (
            !isPeak &&
            ((slugData?.wrdCollection &&
              complianceLabels.harvestLocationRequired) ||
              slugData?.uid === NamedUserUID.SKINS_AND_SCALES) &&
            !value?.trim()
          ) {
            return "Ranch/Outfitter is required";
          }
          return null;
        },
        // The rest of the WRD section is required whenever it's shown. These
        // are searchable Selects and free text that otherwise only carry the
        // browser's required attribute, which typed-but-unpicked text passes.
        state: (value) =>
          slugData?.wrdCollection && !value ? "State is required" : null,
        licenseType: (value) => {
          if (huntingLicenseOnly && value !== "hunting") {
            return "A hunting license is required";
          }
          return slugData?.wrdCollection && !value
            ? "License type is required"
            : null;
        },
        licenseNumber: (value) => {
          if (huntingLicenseOnly && !value?.trim()) {
            return "Hunting license number is required";
          }
          return slugData?.wrdCollection && !value?.trim()
            ? `${complianceLabels.licenseNumberLabel || "License number"} is required`
            : null;
        },
        licenseState: (value) =>
          slugData?.wrdCollection && !value ? "License state is required" : null,
        mosesId: (value) =>
          collectsMaineRecord && !value?.trim()
            ? "MOSES ID is required"
            : null,
        county: (value) => {
          // Top Pin requires county on the WRD section (field only renders
          // when wrdCollection is on, so only validate then)
          if (
            slugData?.uid === NamedUserUID.TOPPIN &&
            slugData?.wrdCollection &&
            !value?.trim()
          ) {
            return "County is required";
          }
          return null;
        },
        hunter: {
          firstName: (value: string, values: any) =>
            hunterFieldsRequired(values) && !value?.trim()
              ? "Hunter's first name is required"
              : null,
          lastName: (value: string, values: any) =>
            hunterFieldsRequired(values) && !value?.trim()
              ? "Hunter's last name is required"
              : null,
          address: {
            streetAddress: (value: string, values: any) =>
              hunterAddressRequired(values) && !value?.trim()
                ? "Hunter's street address is required"
                : null,
            city: (value: string, values: any) =>
              hunterAddressRequired(values) && !value?.trim()
                ? "Hunter's city is required"
                : null,
            state: (value: string, values: any) =>
              hunterAddressRequired(values) && !value
                ? "Hunter's state is required"
                : null,
            zipCode: (value: string, values: any) =>
              hunterAddressRequired(values) && !value?.trim()
                ? "Hunter's zip code is required"
                : null,
          },
        },
        additionalFieldValue: (value: string) => {
          const field = getAdditionalField(slugData);
          if (field && !value?.trim()) {
            return `${field.label} is required`;
          }
          return null;
        },
      },
    },
  });

  // On blur of a searchable Select, adopt typed text that matches an option
  // (see findOptionValueByText for why). Only ever fills, never clears.
  const commitTypedOption = (path: string, value: string | null) => {
    if (value) form.setFieldValue(path, value);
  };

  // Picking a species resets the mount type and price for that line; its
  // mount type options narrow to what the price list has for that species.
  const handleSpeciesChange = (index: number, species: string | null) => {
    form.setFieldValue(`lineItems.${index}.specimen.species`, species);
    form.setFieldValue(`lineItems.${index}.specimen.mountType`, null);
    form.setFieldValue(`lineItems.${index}.price`, null);
  };

  useEffect(() => {
    loadBusinessData();
  }, [slug]);

  // Set address country from slug data
  useEffect(() => {
    if (slugData?.country) {
      const isPrimaryCountry = groupedCountryOptions[0].items.some(
        (opt) => opt === slugData.country
      );
      const addressCountry = isPrimaryCountry ? slugData.country : "other";
      setSelectedAddressCountry(addressCountry);
      form.setFieldValue("client.address.country", slugData.country);
    }
  }, [slugData]);

  // On a partner form the outfitter and the country they hunt are already
  // known, so don't make the hunter type them into the WRD section. All left
  // editable in case the animal was actually taken somewhere else.
  useEffect(() => {
    if (!isPartnerForm) return;

    if (prefillRanchOutfitter && partnerName) {
      form.setFieldValue("extraData.ranchOutfitter", partnerName);
    }
    if (slugData?.defaultCounty) {
      form.setFieldValue("extraData.county", slugData.defaultCounty);
    }
  }, [isPartnerForm, prefillRanchOutfitter, partnerName, slugData]);

  // Harvest state the WRD section starts on. Partner forms carry their own;
  // Mesquite and the demo account only ever book Texas hunts. This has to be
  // a real form value: a defaultValue on the controlled Select shows "Texas"
  // while the submitted value stays empty.
  const defaultHarvestState =
    slugData?.defaultState ||
    (slugData?.uid === NamedUserUID.Mesquite ||
    slugData?.uid === NamedUserUID.ADMIN
      ? "Texas"
      : undefined);

  useEffect(() => {
    if (defaultHarvestState && !(form.values as any).extraData?.state) {
      form.setFieldValue("extraData.state", defaultHarvestState);
    }
  }, [defaultHarvestState]);

  // The WRD wants the state that issued the hunter's license, which is nearly
  // always where they live: prefill from their address state once they pick
  // it, leaving the field editable for the exceptions. Only US address states
  // match the License State options (other countries use abbreviations).
  useEffect(() => {
    if (!slugData?.wrdCollection) return;
    const addressState = form.values.client.address?.state;
    if (
      addressState &&
      !(form.values as any).extraData?.licenseState &&
      usStatesOptions.some((option) => option.value === addressState)
    ) {
      form.setFieldValue("extraData.licenseState", addressState);
    }
  }, [form.values.client.address?.state, slugData?.wrdCollection]);

  // Keep the locked License Type on hunting, including after the form resets
  // for the next booking.
  const licenseType = (form.values as any).extraData?.licenseType;
  useEffect(() => {
    if (huntingLicenseOnly && licenseType !== "hunting") {
      form.setFieldValue("extraData.licenseType", "hunting");
    }
  }, [huntingLicenseOnly, licenseType]);

  // Keep what's been typed through an accidental refresh (Peak ticket #3).
  // Session storage, not local: studios run this on a counter tablet, so a
  // half-finished booking mustn't greet the next customer once the tab is
  // closed. The signature and photos aren't kept and have to be redone.
  const draftKey = `booking-draft:${slug}`;
  // Only something the customer typed or picked is worth keeping: the form
  // fills in country, tax and prefills on its own, which isn't a booking.
  const draftHasContent = (values: any) =>
    !!(
      values?.client?.firstName ||
      values?.client?.lastName ||
      values?.client?.contactDetails?.email ||
      values?.client?.contactDetails?.phone ||
      values?.client?.address?.streetAddress ||
      values?.notes ||
      values?.lineItems?.some(
        (item: any) => item?.specimen?.species || item?.service
      )
    );
  const draftRestoredRef = useRef(false);
  // A restored address must survive the country effect below, which blanks
  // the address whenever the country changes
  const keepAddressRef = useRef(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  useEffect(() => {
    if (loading || !slugData || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(draftKey);
      const draft = raw ? JSON.parse(raw) : null;
      if (
        draftHasContent(draft?.values) &&
        Date.now() - draft.savedAt < 12 * 60 * 60 * 1000
      ) {
        const { client, lineItems, notes, extraData } = draft.values;
        if (
          client?.address?.country &&
          client.address.country !== form.values.client.address?.country
        ) {
          keepAddressRef.current = true;
        }
        if (
          draft.currency &&
          draft.currency !== selectedCurrency &&
          priceList
        ) {
          setSelectedCurrency(draft.currency);
          updateOptionsForCurrency(priceList, draft.currency);
        }
        form.setValues({
          client,
          notes,
          includeTax: draft.includeTax ?? form.values.includeTax,
          // JSON turned the harvest dates into strings
          lineItems: lineItems.map((item: any) =>
            item.specimen?.harvestDate
              ? {
                  ...item,
                  specimen: {
                    ...item.specimen,
                    harvestDate: new Date(item.specimen.harvestDate),
                  },
                }
              : item
          ),
          // @ts-ignore
          extraData: {
            ...(form.values as any).extraData,
            ...extraData,
            signature: "",
          },
        });
        setDraftRestored(true);
      }
    } catch {
      // No storage (private mode) or a bad draft: start blank
    }
    setDraftReady(true);
  }, [loading, slugData]);

  // Reload rather than reset by hand: a fresh load re-applies every
  // studio default (country, tax, partner prefills) the same way first
  // visit does.
  const startOverDraft = () => {
    try {
      sessionStorage.removeItem(draftKey);
    } catch {
      // nothing saved to clear
    }
    setDraftReady(false);
    window.location.reload();
  };

  useEffect(() => {
    if (!draftReady || success) return;
    try {
      if (!draftHasContent(form.values)) {
        sessionStorage.removeItem(draftKey);
        return;
      }
      const { client, lineItems, notes, extraData } = form.values as any;
      sessionStorage.setItem(
        draftKey,
        JSON.stringify({
          savedAt: Date.now(),
          currency: selectedCurrency,
          includeTax: form.values.includeTax,
          values: {
            client,
            lineItems,
            notes,
            extraData: { ...extraData, signature: "" },
          },
        })
      );
    } catch {
      // Storage full or blocked; the form still works without a draft
    }
  }, [form.values, draftReady, success, selectedCurrency]);

  // Update address fields when address country changes
  useEffect(() => {
    if (form.values.client.address?.country) {
      const country = form.values.client.address.country;
      const isPrimaryCountry = groupedCountryOptions[0].items.some(
        (opt) => opt === country
      );

      const newSelectedAddressCountry = isPrimaryCountry ? country : "other";
      const phoneCode = countryCode[
        country as keyof typeof countryCode
      ] as Country;

      // Reset address fields to match new country format, keeping the country value
      const newAddress = {
        ...addressFields[newSelectedAddressCountry].reduce(
          (acc: Record<keyof Address, string>, field) => {
            acc[field.name] = "";
            return acc;
          },
          {} as Record<keyof Address, string>
        ),
        country,
      };

      if (keepAddressRef.current) {
        keepAddressRef.current = false;
      } else {
        form.setFieldValue("client.address", newAddress);
      }
      setSelectedAddressCountry(newSelectedAddressCountry);
      if (phoneCode) {
        setSelectedPhoneCode(phoneCode);
      }
    }
  }, [form.values.client.address?.country]);

  // Update favicon and page title when slug data loads
  useEffect(() => {
    if (slugData) {
      // Update page title
      document.title = `Book | ${slugData.businessName}`;

      // Update favicon
      if (slugData.logoUrl) {
        // Remove existing favicon links
        const existingFavicons = document.querySelectorAll("link[rel*='icon']");
        existingFavicons.forEach((favicon) => favicon.remove());

        // Add new favicon
        const link = document.createElement("link");
        link.rel = "icon";
        link.href = slugData.logoUrl;
        document.head.appendChild(link);
      }
    }

    // Cleanup: restore default title and favicon when component unmounts
    return () => {
      document.title = "MountMonitor";
    };
  }, [slugData]);

  // Auto-update prices from price list when species/mount type changes
  useEffect(() => {
    if (!priceList || !selectedCurrency) return;

    form.values.lineItems.forEach((lineItem, index) => {
      if (isSpecimenLineItem(lineItem)) {
        if (lineItem.specimen.species && lineItem.specimen.mountType) {
          const speciesData = priceList[lineItem.specimen.species];
          if (speciesData && speciesData[selectedCurrency]) {
            const matchingPrice = speciesData[selectedCurrency].find(
              (entry: any) =>
                entry.mount === lineItem.specimen.mountType &&
                entry.type === "specimen"
            );

            if (matchingPrice) {
              if (matchingPrice.chargePerSquareUnit) {
                // Charge-by-unit item: store the per-unit price as the base and
                // let the customer estimate the number of units (defaults to 1).
                // The displayed price is computed by the multiplication effect.
                const existing = lineItem.chargePerSquareUnit;
                const multiplier = existing?.multiplier ?? 1;
                if (
                  !existing ||
                  existing.basePrice !== matchingPrice.price ||
                  existing.multiplier == null
                ) {
                  form.setFieldValue(`lineItems.${index}.chargePerSquareUnit`, {
                    basePrice: matchingPrice.price,
                    multiplier,
                  });
                }
              } else {
                // Flat-price item: clear any per-unit state and set price directly.
                if (lineItem.chargePerSquareUnit) {
                  form.setFieldValue(
                    `lineItems.${index}.chargePerSquareUnit`,
                    null
                  );
                }
                if (lineItem.price !== matchingPrice.price) {
                  form.setFieldValue(
                    `lineItems.${index}.price`,
                    matchingPrice.price
                  );
                }
              }

              // Update deposit settings
              if (
                matchingPrice.depositAmount !== undefined &&
                matchingPrice.depositAmount !== null
              ) {
                if (lineItem.depositAmount !== matchingPrice.depositAmount) {
                  form.setFieldValue(
                    `lineItems.${index}.depositAmount`,
                    matchingPrice.depositAmount
                  );
                }
              }
              if (matchingPrice.depositType) {
                if (lineItem.depositType !== matchingPrice.depositType) {
                  form.setFieldValue(
                    `lineItems.${index}.depositType`,
                    matchingPrice.depositType
                  );
                }
              }
            }
          }
        }
      } else if (isServiceLineItem(lineItem)) {
        if (lineItem.service) {
          const serviceData = priceList[lineItem.service];
          if (serviceData && serviceData[selectedCurrency]) {
            const serviceEntry = serviceData[selectedCurrency].find(
              (entry: any) => entry.type === "service"
            );

            if (serviceEntry) {
              // Update price
              if (lineItem.price !== serviceEntry.price) {
                form.setFieldValue(
                  `lineItems.${index}.price`,
                  serviceEntry.price
                );
              }

              // Update deposit settings
              if (
                serviceEntry.depositAmount !== undefined &&
                serviceEntry.depositAmount !== null
              ) {
                if (lineItem.depositAmount !== serviceEntry.depositAmount) {
                  form.setFieldValue(
                    `lineItems.${index}.depositAmount`,
                    serviceEntry.depositAmount
                  );
                }
              }
              if (serviceEntry.depositType) {
                if (lineItem.depositType !== serviceEntry.depositType) {
                  form.setFieldValue(
                    `lineItems.${index}.depositType`,
                    serviceEntry.depositType
                  );
                }
              }
            }
          }
        }
      }
    });
  }, [
    // Watch for changes to species/mountType/service
    JSON.stringify(
      form.values.lineItems.map((item) =>
        isSpecimenLineItem(item)
          ? {
              species: item.specimen.species,
              mountType: item.specimen.mountType,
            }
          : { service: (item as any).service }
      )
    ),
    priceList,
    selectedCurrency,
  ]);

  // Recompute price for charge-by-unit items when base price or est. units change
  useEffect(() => {
    form.values.lineItems.forEach((lineItem, index) => {
      const charge = lineItem.chargePerSquareUnit;
      if (!charge || charge.basePrice == null || charge.multiplier == null) {
        return;
      }

      const newPrice = calculateMultiplication(
        charge.basePrice,
        charge.multiplier
      );

      if (lineItem.price !== newPrice) {
        form.setFieldValue(`lineItems.${index}.price`, newPrice);
      }
    });
  }, [
    JSON.stringify(
      form.values.lineItems.map((item) => ({
        basePrice: item.chargePerSquareUnit?.basePrice ?? null,
        multiplier: item.chargePerSquareUnit?.multiplier ?? null,
      }))
    ),
  ]);

  // Calculate deposits for each line item when price/depositAmount/depositType changes
  useEffect(() => {
    form.values.lineItems.forEach((lineItem, index) => {
      if (!lineItem.price) {
        // Reset deposit if there's no price
        if (lineItem.deposit) {
          form.setFieldValue(`lineItems.${index}.deposit`, null);
        }
        return;
      }

      // Calculate deposit using precision-safe math
      const depositValue = calculateLineItem.deposit({
        price: lineItem.price,
        depositAmount: lineItem.depositAmount,
        depositType: lineItem.depositType || "percentage",
      });

      // Only update if the value actually changed to avoid infinite loops
      if (lineItem.deposit !== depositValue.raw()) {
        form.setFieldValue(`lineItems.${index}.deposit`, depositValue.raw());
      }
    });
  }, [
    // Watch for changes to any line item's price or deposit settings
    JSON.stringify(
      form.values.lineItems.map((item) => ({
        price: item.price,
        depositAmount: item.depositAmount,
        depositType: item.depositType,
      }))
    ),
  ]);

  // Calculate all job totals whenever line items or tax settings change
  useEffect(() => {
    const lineItems = form.values.lineItems;
    const payments = form.values.payments || [];
    const includeTax = form.values.includeTax;

    // Calculate effective tax rate - use 0 if tax is not included
    const effectiveTaxRate = includeTax ? form.values.taxRate || 0 : 0;

    /**
     * Calculate all financial totals using our precision-safe math helpers
     * Each calculation returns a DecimalValue that maintains precision
     * We use .raw() to convert to number for form storage
     */
    const calculations = {
      // Basic totals
      subtotal: calculateJob.subtotal(lineItems).raw(),
      totalDiscount: calculateJob.totalDiscount(lineItems).raw(),
      subtotalAfterDiscounts: calculateJob
        .subtotalAfterDiscounts(lineItems)
        .raw(),

      // Tax calculations
      totalTax: calculateJob.tax(lineItems, effectiveTaxRate, includeTax).raw(),

      // Final totals
      grandTotal: calculateJob
        .grandTotal(lineItems, effectiveTaxRate, includeTax)
        .raw(),

      // Deposit and balance
      totalDeposit: calculateJob.totalDeposit(lineItems).raw(),
      remainingBalance: calculateJob
        .remainingBalance(lineItems, effectiveTaxRate, payments, includeTax)
        .raw(),
    };

    /**
     * Update all form fields with calculated values
     * Using explicit setFieldValue calls to ensure type safety
     */
    form.setFieldValue("subtotal", calculations.subtotal);
    form.setFieldValue("totalDiscount", calculations.totalDiscount);
    form.setFieldValue(
      "subtotalAfterDiscounts",
      calculations.subtotalAfterDiscounts
    );
    form.setFieldValue("totalTax", calculations.totalTax);
    form.setFieldValue("grandTotal", calculations.grandTotal);
    form.setFieldValue("totalDeposit", calculations.totalDeposit);
    form.setFieldValue("remainingBalance", calculations.remainingBalance);
  }, [form.values.lineItems, form.values.payments, form.values.includeTax]);

  const loadBusinessData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get slug data

      const slugDoc = await getDoc(doc(db, "slugs", slug));

      if (!slugDoc.exists()) {
        setError("Business not found");
        setLoading(false);
        return;
      }

      const slugInfo = slugDoc.data() as SlugData;

      setSlugData(slugInfo);
      setSelectedCurrency(slugInfo.currency);

      // Set client currency from business currency
      if (slugInfo.currency) {
        const currencyDetails = getCurrencyDetailsByCode(slugInfo.currency);
        if (currencyDetails) {
          form.setFieldValue("client.currency", currencyDetails);
        }
      }

      // Set tax rate and includeTax from slug data
      // For ALLOVER, tax is conditional on NZD + NZ phone — don't auto-enable
      if (slugInfo.taxRate !== undefined) {
        form.setFieldValue("taxRate", slugInfo.taxRate);
        if (slugInfo.uid !== NamedUserUID.ALLOVER) {
          form.setFieldValue("includeTax", true);
        }
      }

      // Set default phone country based on business country
      if (slugInfo.country) {
        const phoneCode = countryCode[
          slugInfo.country as keyof typeof countryCode
        ] as Country;
        setSelectedPhoneCode(phoneCode);
      }

      // Load price lists and extract available options
      const priceListDoc = await getDoc(
        doc(db, `users/${slugInfo.uid}/metadata/priceList`)
      );

      if (priceListDoc.exists()) {
        const priceListData = priceListDoc.data();
        setPriceList(priceListData);
        updateOptionsForCurrency(priceListData, slugInfo.currency);
      }

      // Live studio details. The slug doc is a copy that goes stale when the
      // studio changes their tax rate, address or mount types elsewhere in
      // settings, so these win over it; a failed request keeps the slug copy.
      try {
        const res = await fetch(
          `/api/book/studio?slug=${encodeURIComponent(slug)}`
        );
        if (res.ok) {
          const { taxRate, state, mountTypeOrder } = await res.json();
          if (Array.isArray(mountTypeOrder)) setMountTypeOrder(mountTypeOrder);
          if (typeof taxRate === "number") {
            form.setFieldValue("taxRate", taxRate);
          }
          setSlugData((prev) =>
            prev
              ? {
                  ...prev,
                  ...(typeof taxRate === "number" ? { taxRate } : {}),
                  ...(typeof state === "string" ? { state } : {}),
                }
              : prev
          );
        }
      } catch {
        // keep the slug's copies
      }

      setLoading(false);
    } catch (err) {
      setError("Failed to load booking form");
      setLoading(false);
    }
  };

  const calculatePrice = (item: JobSpecimenLineItem): number => {
    if (!priceList || !selectedCurrency) return 0;

    try {
      // Look for species in price list
      const speciesData = priceList[item.specimen.species];
      if (!speciesData) return 0;

      // Get prices for the selected currency
      const currencyPrices = speciesData[selectedCurrency];
      if (!currencyPrices || !Array.isArray(currencyPrices)) return 0;

      // Find matching mount type
      const matchingPrice = currencyPrices.find(
        (entry: any) =>
          entry.mount === item.specimen.mountType && entry.type === "specimen"
      );

      return matchingPrice?.price || 0;
    } catch (error) {
      return 0;
    }
  };

  const calculateServicePrice = (serviceName: string): number => {
    if (!priceList || !selectedCurrency) return 0;

    try {
      // Look for service in price list
      const serviceData = priceList[serviceName];
      if (!serviceData) return 0;

      // Get prices for the selected currency
      const currencyPrices = serviceData[selectedCurrency];
      if (!currencyPrices || !Array.isArray(currencyPrices)) return 0;

      // Services should have a single entry in the array
      const serviceEntry = currencyPrices.find(
        (entry: any) => entry.type === "service"
      );

      return serviceEntry?.price || 0;
    } catch (error) {
      return 0;
    }
  };

  const calculateTotal = (): number => {
    let total = 0;
    form.values.lineItems.forEach((item) => {
      if (isSpecimenLineItem(item)) {
        const price = calculatePrice(item);
        total = calculateAddition(total, price);
      } else if (isServiceLineItem(item)) {
        const price = calculateServicePrice(item.service);
        total = calculateAddition(total, price);
      }
    });
    return total;
  };

  // Order mount types the way the business arranged them in settings;
  // names not in that list (and everything when it's empty) sort
  // alphabetically after the ordered ones.
  const sortMountTypes = (types: string[]): string[] => {
    const position = (name: string) => {
      const i = mountTypeOrder.indexOf(name);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...types].sort(
      (a, b) => position(a) - position(b) || a.localeCompare(b)
    );
  };

  // Update available mount types for selected species
  const updateMountTypesForSpecies = (species: string) => {
    if (!priceList || !selectedCurrency) return [];

    // If "Other" is selected, return all available mount types from the price list
    if (species === "Other") {
      const allMountTypes = new Set<string>();

      // Collect all mount types from all species in the price list
      Object.values(priceList).forEach((speciesData: any) => {
        if (speciesData[selectedCurrency]) {
          speciesData[selectedCurrency]
            .filter((entry: any) => entry.type === "specimen")
            .forEach((entry: any) => {
              if (entry.mount) {
                allMountTypes.add(entry.mount);
              }
            });
        }
      });

      return sortMountTypes([...allMountTypes]);
    }

    const speciesData = priceList[species];
    if (!speciesData || !speciesData[selectedCurrency]) return [];

    const mountTypesForSpecies = speciesData[selectedCurrency]
      .filter((entry: any) => entry.type === "specimen")
      .map((entry: any) => entry.mount)
      .filter(Boolean); // Remove any null/undefined values

    return sortMountTypes([...new Set(mountTypesForSpecies)] as string[]);
  };

  const updateOptionsForCurrency = (priceListData: any, currency: string) => {
    const species: string[] = [];
    const services: string[] = [];

    Object.entries(priceListData).forEach(
      ([itemName, itemData]: [string, any]) => {
        if (itemData[currency] && Array.isArray(itemData[currency])) {
          const entries = itemData[currency];
          entries.forEach((entry: any) => {
            if (entry.type === "specimen") {
              species.push(itemName);
            } else if (entry.type === "service") {
              services.push(itemName);
            }
          });
        }
      }
    );

    const sortedSpecies = [...new Set(species)].sort((a, b) =>
      a.localeCompare(b)
    );
    setSpeciesOptions([...sortedSpecies, "Other"]);
    setServiceOptions(
      [...new Set(services)].sort((a, b) => a.localeCompare(b))
    );
  };

  const handleCurrencyChange = (
    currency: string,
    phoneCodeOverride?: Country
  ) => {
    setSelectedCurrency(currency);
    const currencyDetails = getCurrencyDetailsByCode(currency);
    if (currencyDetails) {
      form.setFieldValue("client.currency", currencyDetails);
    }

    // NZD includes GST, but only if the customer is also from NZ.
    // ALLOVER never charges tax regardless of currency/phone.
    const phoneCode = phoneCodeOverride ?? selectedPhoneCode;
    if (
      slugData?.uid !== NamedUserUID.ALLOVER &&
      currency === "NZD" &&
      phoneCode === "NZ"
    ) {
      form.setFieldValue("includeTax", true);
      if (slugData?.taxRate !== undefined) {
        form.setFieldValue("taxRate", slugData.taxRate);
      }
    } else {
      form.setFieldValue("includeTax", false);
    }

    // Re-filter species/services for the new currency and reset line items
    if (priceList) {
      updateOptionsForCurrency(priceList, currency);
      form.setFieldValue("lineItems", [generateDefaultSpecimenLineItem()]);
    }
  };

  // Mantine stamps data-error on any input whose error prop is set, so the
  // first one in document order is the first invalid field on the page. Wait a
  // frame so the errors have rendered before looking.
  const scrollToFirstError = () => {
    requestAnimationFrame(() => {
      document
        .querySelector('[data-error="true"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  console.log(slugData);

  const hunterIsBooker = (form.values as any).extraData?.isHunter !== false;
  const hunterSameAddress = !!(form.values as any).extraData
    ?.hunterSameAddress;

  const handleSubmit = async (values: JobV2) => {
    if (!slugData) return;

    // Check if form has errors and scroll to first error
    if (!form.isValid()) {
      console.log("Form is invalid, errors:", form.errors);
      scrollToFirstError();
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch("/api/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userUid: slugData.uid,
          job: values,
          wrdCollection: slugData.wrdCollection || false,
          // The API re-reads the slug doc to resolve a partner form's fixed
          // client, so that can't be forced from here.
          slug,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit booking");
      }

      try {
        sessionStorage.removeItem(draftKey);
      } catch {
        // nothing saved to clear
      }
      setDraftRestored(false);
      setSuccess(true);
      form.reset();
      // reset() restores initialValues INCLUDING the original uid; the API
      // keys the pendingJobs doc by that uid (deliberate double-submit
      // idempotency), so without a fresh uid the next booking from this page
      // session would overwrite this one.
      form.setFieldValue("uid", generateUid());
      // reset() also clears the prefilled WRD harvest details; put them back
      // for the next booking taken on the same device.
      if (isPartnerForm) {
        if (prefillRanchOutfitter && partnerName) {
          form.setFieldValue("extraData.ranchOutfitter", partnerName);
        }
        if (slugData.defaultCounty) {
          form.setFieldValue("extraData.county", slugData.defaultCounty);
        }
      }
      if (defaultHarvestState) {
        form.setFieldValue("extraData.state", defaultHarvestState);
      }
    } catch (err) {
      console.error("Error submitting booking:", err);
      setError("Failed to submit booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const addMount = () => {
    // Pass the current items so each new mount gets the next ID (M2, M3...)
    // instead of every mount on the booking being "M1".
    const newSpecimenItem = generateDefaultSpecimenLineItem(
      form.values.lineItems
    );
    form.insertListItem("lineItems", newSpecimenItem);
  };

  const removeMount = (index: number) => {
    form.removeListItem("lineItems", index);
  };

  const addService = () => {
    const newServiceItem = generateDefaultServiceLineItem();
    form.insertListItem("lineItems", newServiceItem);
  };

  const removeService = (index: number) => {
    form.removeListItem("lineItems", index);
  };

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (value: string): string => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  useEffect(() => {
    // If email has a value, clear phone error (because email satisfies the
    // requirement). Not for Top Pin: phone is required outright there.
    if (
      form.values.client.contactDetails.email &&
      slugData?.uid !== NamedUserUID.TOPPIN
    ) {
      form.clearFieldError("client.contactDetails.phone");
    }

    // If phone has a value, clear email error (because phone satisfies the requirement)
    if (form.values.client.contactDetails.phone) {
      form.clearFieldError("client.contactDetails.email");
    }
  }, [
    form.values.client.contactDetails.phone,
    form.values.client.contactDetails.email,
  ]);

  // Acuity's embed script listens for the scheduler's postMessage and grows the
  // iframe to fit, so the calendar scrolls with the page instead of inside a box.
  useEffect(() => {
    if (!success || !isPattons) return;

    const script = document.createElement("script");
    script.src = "https://embed.acuityscheduling.com/js/embed.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, [success, isPattons]);

  if (loading) {
    return (
      <Container size="sm" py="xl">
        <LoadingOverlay visible />
      </Container>
    );
  }

  if (error && !slugData) {
    return (
      <Container size="sm" py="xl">
        <Alert icon={<IconAlertCircle />} color="red">
          {error}
        </Alert>
      </Container>
    );
  }

  if (success) {
    // Pattons' confirmation runs straight into the embedded calendar, so it sits
    // tighter than the standard thank-you screen to keep the booking near the top.
    const framePadding = isPattons ? "md" : "xl";
    const sectionGap = isPattons ? "md" : "xl";

    return (
      <ScrollArea h="100dvh" style={{ backgroundColor: primaryColor }}>
        <Container
          size={isPattons ? "md" : "sm"}
          px={{ base: 6, sm: "md" }}
          py={{ base: 6, sm: framePadding }}
        >
          <Paper p={{ base: "sm", sm: framePadding }} radius="md" withBorder>
            <Stack gap={sectionGap}>
              <Stack
                bg={primaryColor}
                py={isPattons ? "xs" : "lg"}
                gap="sm"
                style={{ borderRadius: "2px" }}
                align="center"
              >
                {(slugData?.logoUrl || slugData?.partnerLogoUrl) && (
                  <Group justify="center" gap="lg" wrap="nowrap">
                    {slugData?.logoUrl && (
                      <Image
                        src={slugData.logoUrl}
                        alt={slugData.businessName}
                        w={isPartnerForm ? 96 : 128}
                        fit="contain"
                        c={secondaryColor}
                        style={{ borderRadius: "50%" }}
                      />
                    )}
                    {slugData?.partnerLogoUrl && (
                      <Image
                        src={slugData.partnerLogoUrl}
                        alt={partnerName}
                        w={96}
                        fit="contain"
                        c={secondaryColor}
                        style={{ borderRadius: "50%" }}
                      />
                    )}
                  </Group>
                )}

                {!isPattons && (
                  <Title order={2} ta="center" c={secondaryColor}>
                    {slugData?.businessName}
                  </Title>
                )}
              </Stack>

              <Stack gap={isPattons ? "md" : "lg"} align="center">
                {!isPattons && (
                  <>
                    <Text size="lg" ta="center">
                      We have received your booking request and will review it
                      shortly. We&apos;ll get back to you with a final invoice
                      and confirm any details required about poses
                      {form.values.lineItems.filter(isServiceLineItem).length >
                      0
                        ? ", services,"
                        : ""}{" "}
                      and specifications for your mounts.
                    </Text>
                    <Text
                      size="lg"
                      fw={600}
                      ta="center"
                      c={secondaryColor}
                      mb="md"
                    >
                      Thank You!
                    </Text>
                  </>
                )}

                {isPattons ? (
                  <Stack gap="md" w="100%">
                    <Stack gap={6} align="center">
                      <Title order={3} ta="center" c={secondaryColor}>
                        We&apos;re reviewing your request
                      </Title>
                      <Text size="sm" c="dimmed" ta="center" maw={460}>
                        We&apos;ll follow up with your final invoice. In the
                        meantime, please book your drop-off time below.
                      </Text>
                    </Stack>

                    <Box
                      style={{
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: "var(--mantine-radius-md)",
                        overflow: "hidden",
                      }}
                    >
                      <iframe
                        src={PATTONS_SCHEDULER_EMBED_URL}
                        title="Schedule your drop-off time"
                        width="100%"
                        height={800}
                        frameBorder="0"
                        allow="payment"
                        style={{ display: "block", border: "none" }}
                      />
                    </Box>

                    <Anchor
                      href={PATTONS_SCHEDULING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      ta="center"
                      size="sm"
                      c="dimmed"
                    >
                      Calendar not loading? Open the scheduling page instead
                    </Anchor>
                  </Stack>
                ) : (
                  <Button
                    onClick={() => setSuccess(false)}
                    bg={primaryColor}
                    c={secondaryColor}
                    size="md"
                    style={{
                      border: `${
                        primaryColor === "#ffffff"
                          ? "1px solid " + secondaryColor
                          : "inherit"
                      }`,
                    }}
                  >
                    Submit Another Request
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </ScrollArea>
    );
  }

  return (
    <Stack>
      <ScrollArea h="100dvh" style={{ backgroundColor: primaryColor }}>
        {/* Small phones need every pixel of width: the brand colour shrinks
            to a thin frame and the card padding tightens below sm */}
        <Container
          size="sm"
          px={{ base: 6, sm: "md" }}
          py={{ base: 6, sm: "xl" }}
        >
          <Paper p={{ base: "sm", sm: "xl" }} radius="md" withBorder>
            <Stack gap="lg">
              <Stack
                bg={primaryColor}
                p={{ base: "sm", sm: "lg" }}
                gap="sm"
                style={{ borderRadius: "2px" }}
              >
                {(slugData?.logoUrl || slugData?.partnerLogoUrl) && (
                  <Group justify="center" gap="lg" wrap="nowrap">
                    {slugData?.logoUrl && (
                      <Image
                        src={slugData.logoUrl}
                        alt={slugData.businessName}
                        height={isPartnerForm ? 96 : 128}
                        fit="contain"
                        c={secondaryColor}
                      />
                    )}
                    {slugData?.partnerLogoUrl && (
                      <Image
                        src={slugData.partnerLogoUrl}
                        alt={partnerName}
                        height={96}
                        fit="contain"
                        c={secondaryColor}
                      />
                    )}
                  </Group>
                )}

                <Title order={2} ta="center" c={secondaryColor}>
                  Book with {slugData?.businessName}
                </Title>

                {isPartnerForm && partnerName && (
                  <Text size="sm" ta="center" c={secondaryColor} opacity={0.85}>
                    Booking through {partnerName}
                  </Text>
                )}
              </Stack>
              {/* @ts-ignore */}
              <form onSubmit={form.onSubmit(handleSubmit, scrollToFirstError)}>
                <Stack gap="xl" mt="md">
                  {/* Closing it any other way keeps the draft, same as
                      Continue */}
                  <Modal
                    opened={draftRestored}
                    onClose={() => setDraftRestored(false)}
                    title="Continue your booking?"
                    centered
                    radius="md"
                  >
                    <Stack gap="md">
                      <Text size="sm">
                        You have an unfinished booking. Pick up where you
                        left off?
                      </Text>
                      <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={startOverDraft}>
                          Start over
                        </Button>
                        <Button onClick={() => setDraftRestored(false)}>
                          Continue
                        </Button>
                      </Group>
                    </Stack>
                  </Modal>
                  {additionalField && (
                    <>
                      <TextInput
                        name="extraData.additionalFieldValue"
                        label={additionalField.label}
                        description={additionalField.description || undefined}
                        required
                        size="md"
                        {...form.getInputProps("extraData.additionalFieldValue")}
                      />
                      <Divider />
                    </>
                  )}
                  <Grid>
                    <Grid.Col span={12}>
                      <Title order={4}>Contact Information</Title>
                      {isBilledToPartner && partnerName && (
                        <Text size="sm" c="dimmed" mt={4}>
                          Details of the hunter this work is for. Billing and
                          invoicing go to {partnerName}.
                        </Text>
                      )}
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="First Name"
                        description={
                          slugData?.uid === NamedUserUID.ALLOVER
                            ? "as in passport"
                            : undefined
                        }
                        required
                        size="md"
                        {...form.getInputProps("client.firstName")}
                        onChange={(e) => {
                          const capitalized = capitalizeFirstLetter(
                            e.currentTarget.value
                          );
                          form.setFieldValue("client.firstName", capitalized);
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Last Name"
                        description={
                          slugData?.uid === NamedUserUID.ALLOVER
                            ? "as in passport"
                            : undefined
                        }
                        required
                        size="md"
                        {...form.getInputProps("client.lastName")}
                        onChange={(e) => {
                          const capitalized = capitalizeFirstLetter(
                            e.currentTarget.value
                          );
                          form.setFieldValue("client.lastName", capitalized);
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <PhoneInput
                        required={slugData?.uid === NamedUserUID.TOPPIN}
                        selectedPhoneCode={selectedPhoneCode}
                        form={
                          {
                            getInputProps: (field: string) => {
                              if (field === "phone") {
                                return {
                                  value:
                                    form.values.client.contactDetails.phone,
                                  onChange: (value: string) =>
                                    form.setFieldValue(
                                      "client.contactDetails.phone",
                                      value
                                    ),
                                  // onBlur: () =>
                                  //   form.validateField(
                                  //     "client.contactDetails.phone"
                                  //   ),
                                  onFocus: () => {},
                                  error:
                                    form.errors["client.contactDetails.phone"],
                                };
                              }
                              return form.getInputProps(field);
                            },
                            values: {
                              phone: form.values.client.contactDetails.phone,
                            },
                            errors: {
                              phone: form.errors["client.contactDetails.phone"],
                            },
                          } as any
                        }
                        onCountryChange={(phoneCountryCode) => {
                          setSelectedPhoneCode(phoneCountryCode);
                          const countryInfo =
                            countryToCurrency[phoneCountryCode];
                          if (countryInfo) {
                            form.setFieldValue(
                              "client.address.country",
                              countryInfo.name
                            );
                          }
                          // For ALLOVER: auto-switch currency if the country's currency exists in the price list
                          if (
                            slugData?.uid === NamedUserUID.ALLOVER &&
                            countryInfo &&
                            priceList
                          ) {
                            const countryCurrency = countryInfo.code;
                            const hasCurrencyInPriceList = Object.values(
                              priceList
                            ).some(
                              (item: any) =>
                                item[countryCurrency] &&
                                Array.isArray(item[countryCurrency])
                            );
                            if (hasCurrencyInPriceList) {
                              handleCurrencyChange(
                                countryCurrency,
                                phoneCountryCode
                              );
                            }
                          }
                        }}
                        size="md"
                        flagOnly
                        hideFlag={slugData.uid === NamedUserUID.Mesquite}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        w="100%"
                        label="Email"
                        placeholder="john@example.com"
                        size="md"
                        {...form.getInputProps("client.contactDetails.email")}
                      />
                    </Grid.Col>
                  </Grid>
                  {slugData?.requestAddress !== false && (
                    <>
                      <Divider />
                      <Grid>
                        <Grid.Col span={12}>
                          <Title order={4}>Address</Title>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            required
                            label="Street Address"
                            placeholder="Enter street address"
                            size="md"
                            {...form.getInputProps(
                              "client.address.streetAddress"
                            )}
                          />
                        </Grid.Col>

                        {addressFields[
                          selectedAddressCountry as keyof typeof addressFields
                        ]
                          .filter((field) => field.name !== "streetAddress")
                          .filter(
                            (field) => !(isPeak && field.name === "county")
                          )
                          .map((field) => {
                            const isRequired =
                              field.required !== false &&
                              field.name.toLowerCase() !== "county";
                            switch (field.type) {
                              case "text":
                                return (
                                  <Grid.Col
                                    key={field.name}
                                    span={{ base: 12, md: 6 }}
                                  >
                                    <TextInput
                                      required={isRequired}
                                      size="md"
                                      label={field.label}
                                      placeholder={
                                        "Enter " + field.label.toLowerCase()
                                      }
                                      {...form.getInputProps(
                                        `client.address.${field.name}`
                                      )}
                                    />
                                  </Grid.Col>
                                );
                              case "select":
                                return (
                                  <Grid.Col
                                    key={field.name}
                                    span={{ base: 12, md: 6 }}
                                  >
                                    <Select
                                      required={isRequired}
                                      size="md"
                                      id={`client-address-${field.name}`}
                                      label={field.label}
                                      placeholder={
                                        "Select " + field.label.toLowerCase()
                                      }
                                      searchable
                                      data={field.options}
                                      {...form.getInputProps(
                                        `client.address.${field.name}`
                                      )}
                                      // Browser autofill types "ME" into the
                                      // box without ever focusing it, so the
                                      // blur below never runs and the state
                                      // stays blank. Adopt a match when the
                                      // text changes while someone else has
                                      // focus; live typing waits for blur.
                                      onSearchChange={(text) => {
                                        const id = `client-address-${field.name}`;
                                        if (document.activeElement?.id === id) {
                                          return;
                                        }
                                        const match = findUsStateByText(
                                          field.options || [],
                                          text
                                        );
                                        const path = `client.address.${field.name}`;
                                        if (
                                          match &&
                                          match !==
                                            (form.values.client.address as any)?.[
                                              field.name
                                            ]
                                        ) {
                                          form.setFieldValue(path, match);
                                        }
                                      }}
                                      onBlur={(e) =>
                                        commitTypedOption(
                                          `client.address.${field.name}`,
                                          findUsStateByText(
                                            field.options || [],
                                            e.currentTarget.value
                                          )
                                        )
                                      }
                                    />
                                  </Grid.Col>
                                );
                              default:
                                return null;
                            }
                          })}
                      </Grid>
                    </>
                  )}
                  <Divider />
                  {/* <Radio.Group
                  size="md"
                  label={
                    <Title fw={600} order={4} mb="xs">
                      Delivery Method
                    </Title>
                  }
                  {...form.getInputProps("shipping")}
                >
                  <Group>
                    <Radio value="Pickup" label="Pickup" />
                    <Radio value="Delivery" label="Delivery" />
                  </Group>
                </Radio.Group> */}

                  {/* {form.values.shipping === "Delivery" && (
                  <>
                    <TextInput
                      label="Street Address"
                      size="md"
                      {...form.getInputProps("address.street")}
                    />
                    <Grid>
                      <Grid.Col span={4}>
                        <TextInput
                          label="City"
                          size="md"
                          {...form.getInputProps("address.city")}
                        />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <TextInput
                          label="State"
                          size="md"
                          {...form.getInputProps("address.state")}
                        />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <TextInput
                          label="ZIP"
                          size="md"
                          {...form.getInputProps("address.zip")}
                        />
                      </Grid.Col>
                    </Grid>
                  </>
                )} */}

                  {slugData?.uid === NamedUserUID.ALLOVER && (
                    <Select
                      label="Currency"
                      description="You will be quoted & billed in the currency you select"
                      size="md"
                      required
                      data={
                        priceList
                          ? [
                              ...new Set(
                                Object.values(priceList).flatMap((item: any) =>
                                  Object.keys(item).filter((key) =>
                                    Array.isArray(item[key])
                                  )
                                )
                              ),
                            ].sort()
                          : []
                      }
                      value={selectedCurrency}
                      onChange={(value) => {
                        if (value) handleCurrencyChange(value);
                      }}
                    />
                  )}

                  <Stack gap={0}>
                    <Title order={4}>Mounts</Title>
                    <Text c="dimmed" size="sm">
                      Animals to mount (have or plan to hunt)
                    </Text>
                  </Stack>
                  {form.values.lineItems
                    .filter(isSpecimenLineItem)
                    .map((item, index) => {
                      const realIndex = form.values.lineItems.indexOf(item);
                      // Per mount, so a second mount of another species
                      // doesn't blank this one's mount type
                      const itemMountTypes = item.specimen.species
                        ? updateMountTypesForSpecies(item.specimen.species)
                        : [];
                      return (
                        <Paper key={item.uid} p={{ base: "sm", sm: "md" }} withBorder>
                          <Stack gap="sm">
                            <Group justify="space-between">
                              <Text size="sm" fw={500}>
                                Mount #{index + 1}
                              </Text>
                              {form.values.lineItems.filter(isSpecimenLineItem)
                                .length > 1 && (
                                <ActionIcon
                                  color="red"
                                  onClick={() => removeMount(realIndex)}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              )}
                            </Group>

                            <Grid>
                              <Grid.Col
                                span={{
                                  base: 12,
                                  md:
                                    slugData.uid === NamedUserUID.SAFARISTUDIO
                                      ? 6
                                      : 12,
                                }}
                              >
                                <Select
                                  label="Species"
                                  required
                                  searchable
                                  size="md"
                                  data={speciesOptions}
                                  value={item.specimen.species}
                                  error={
                                    form.errors[
                                      `lineItems.${realIndex}.specimen.species`
                                    ]
                                  }
                                  onBlur={(e) => {
                                    const typed = findOptionValueByText(
                                      speciesOptions,
                                      e.currentTarget.value
                                    );
                                    if (typed && typed !== item.specimen.species) {
                                      handleSpeciesChange(realIndex, typed);
                                    }
                                  }}
                                  onChange={(value) =>
                                    handleSpeciesChange(realIndex, value)
                                  }
                                />
                              </Grid.Col>
                              <Grid.Col
                                span={{
                                  base: 12,
                                  md:
                                    slugData.uid === NamedUserUID.Mesquite
                                      ? 12
                                      : slugData.uid === NamedUserUID.ALLOVER
                                      ? 12
                                      : 6,
                                }}
                              >
                                <Select
                                  label="Mount Type"
                                  required={item.specimen.species !== "Other"}
                                  searchable
                                  size="md"
                                  data={itemMountTypes}
                                  disabled={!item.specimen.species}
                                  placeholder="Select mount type"
                                  error={
                                    form.errors[
                                      `lineItems.${realIndex}.specimen.mountType`
                                    ]
                                  }
                                  value={item.specimen.mountType}
                                  onBlur={(e) =>
                                    commitTypedOption(
                                      `lineItems.${realIndex}.specimen.mountType`,
                                      findOptionValueByText(
                                        itemMountTypes,
                                        e.currentTarget.value
                                      )
                                    )
                                  }
                                  onChange={(value) => {
                                    form.setFieldValue(
                                      `lineItems.${realIndex}.specimen.mountType`,
                                      value
                                    );
                                    // Price will be auto-updated by useEffect
                                  }}
                                />
                              </Grid.Col>
                              <Grid.Col
                                span={{
                                  base: 12,
                                  md:
                                    slugData.uid === NamedUserUID.ALLOVER
                                      ? 6
                                      : 6,
                                }}
                                hidden={slugData.uid === NamedUserUID.Mesquite}
                              >
                                <Select
                                  label="Pose"
                                  required={
                                    slugData.uid === NamedUserUID.SAFARISTUDIO
                                  }
                                  size="md"
                                  data={
                                    slugData.uid === NamedUserUID.WHITE_OAK
                                      ? whiteOakMainPoseOptions
                                      : slugData.uid === NamedUserUID.Mesquite
                                      ? mesquiteMainPoseOptions
                                      : slugData.uid === NamedUserUID.MYRICK
                                      ? myrickMainPoseOptions
                                      : slugData.uid ===
                                        NamedUserUID.SAFARISTUDIO
                                      ? safariStudioMainPoseOptions
                                      : slugData.uid === NamedUserUID.TOPPIN
                                      ? toppinMainPoseOptions
                                      : slugData.uid === NamedUserUID.BONEZ
                                      ? bonezMainPoseOptions
                                      : slugData.uid ===
                                        NamedUserUID.CL_WILDLIFE_DESIGN
                                      ? clWildlifeMainPoseOptions
                                      : slugData.uid ===
                                        NamedUserUID.TAXIDERMY_DESIGNS
                                      ? taxidermyDesignsMainPoseOptions
                                      : isPeak
                                      ? peakMainPoseOptions
                                      : mainPoseOptions
                                  }
                                  disabled={!item.specimen.species}
                                  placeholder="Select pose"
                                  error={
                                    form.errors[
                                      `lineItems.${realIndex}.specimen.mainPose`
                                    ]
                                  }
                                  value={item.specimen.mainPose}
                                  onChange={(value) =>
                                    form.setFieldValue(
                                      `lineItems.${realIndex}.specimen.mainPose`,
                                      value
                                    )
                                  }
                                />
                              </Grid.Col>
                              {isPeak &&
                                (["leftEar", "rightEar"] as const).map(
                                  (ear) => (
                                    <Grid.Col
                                      key={ear}
                                      span={{ base: 12, md: 6 }}
                                    >
                                      <Select
                                        label={
                                          ear === "leftEar"
                                            ? "Left Ear"
                                            : "Right Ear"
                                        }
                                        size="md"
                                        data={peakEarOptions}
                                        disabled={!item.specimen.species}
                                        placeholder="Select ear position"
                                        value={(item as any)[ear] || null}
                                        onChange={(value) =>
                                          form.setFieldValue(
                                            `lineItems.${realIndex}.${ear}`,
                                            value
                                          )
                                        }
                                      />
                                    </Grid.Col>
                                  )
                                )}
                              <Grid.Col span={{ base: 12, md: 6 }}>
                                  <Select
                                    label="Direction"
                                    required={
                                      slugData.uid === NamedUserUID.SAFARISTUDIO
                                    }
                                    size="md"
                                    data={
                                      slugData.uid ===
                                      NamedUserUID.SAFARISTUDIO
                                        ? safariStudioDirectionOptions
                                        : slugData.uid === NamedUserUID.TRUELIFE
                                        ? trueLifeDirectionOptions
                                        : directionOptions
                                    }
                                    disabled={!item.specimen.species}
                                    placeholder="Select direction"
                                    error={
                                      form.errors[
                                        `lineItems.${realIndex}.specimen.direction`
                                      ]
                                    }
                                    value={item.specimen.direction || null}
                                    onChange={(value) =>
                                      form.setFieldValue(
                                        `lineItems.${realIndex}.specimen.direction`,
                                        value
                                      )
                                    }
                                  />
                                </Grid.Col>
                            </Grid>

                            {!slugData?.hidePricing &&
                              item.specimen.species !== "Other" &&
                              item.chargePerSquareUnit?.basePrice != null && (
                                <NumberInput
                                  label="Estimated units"
                                  description={`This mount is priced in ${possibleUnits}.`}
                                  size="md"
                                  min={1}
                                  allowDecimal
                                  value={
                                    item.chargePerSquareUnit.multiplier ?? 1
                                  }
                                  onChange={(value) =>
                                    form.setFieldValue(
                                      `lineItems.${realIndex}.chargePerSquareUnit.multiplier`,
                                      typeof value === "number" ? value : 1
                                    )
                                  }
                                />
                              )}

                            {!isPeak && (
                              <DatePickerInput
                                required
                                clearable
                                label="Harvest Date"
                                placeholder="Pick Harvest Date"
                                maxDate={new Date()}
                                size="md"
                                value={
                                  item.specimen.harvestDate
                                    ? item.specimen.harvestDate instanceof Date
                                      ? item.specimen.harvestDate
                                      : new Date(
                                          item.specimen.harvestDate.toDate()
                                        )
                                    : null
                                }
                                onChange={(value) =>
                                  form.setFieldValue(
                                    `lineItems.${realIndex}.specimen.harvestDate`,
                                    value
                                  )
                                }
                              />
                            )}

                            {collectsSpecimenSex && (
                              <Select
                                required
                                label="Sex"
                                placeholder="Select sex"
                                size="md"
                                data={SPECIMEN_SEX_OPTIONS}
                                disabled={!item.specimen.species}
                                {...form.getInputProps(
                                  `lineItems.${realIndex}.specimenSex`
                                )}
                                value={(item as any).specimenSex || null}
                              />
                            )}

                            {collectsMaineRecord && (
                              <TextInput
                                label="Seal / Tag #"
                                description="The seal number issued when this animal was registered. Leave blank if it did not need one."
                                placeholder="Seal or tag number"
                                size="md"
                                disabled={!item.specimen.species}
                                value={(item as any).sealNumber || ""}
                                onChange={(e) =>
                                  form.setFieldValue(
                                    `lineItems.${realIndex}.sealNumber`,
                                    e.target.value
                                  )
                                }
                              />
                            )}

                            <Textarea
                              label="Notes"
                              placeholder={
                                item.specimen.species === "Other"
                                  ? "Please tell us the relevant details around the mount..."
                                  : "Any special requests..."
                              }
                              size="md"
                              required={item.specimen.species === "Other"}
                              disabled={!item.specimen.species}
                              rows={4}
                              value={item.notes}
                              onChange={(e) =>
                                form.setFieldValue(
                                  `lineItems.${realIndex}.notes`,
                                  e.target.value
                                )
                              }
                            />

                            {!slugData?.hidePricing &&
                              item.specimen.species &&
                              (item.specimen.mountType ||
                                item.specimen.species === "Other") && (
                                <Stack gap="xs">
                                  {item.specimen.species !== "Other" &&
                                    item.chargePerSquareUnit?.basePrice !=
                                      null && (
                                      <Group justify="space-between">
                                        <Text size="sm" c="dimmed">
                                          Price per unit:
                                        </Text>
                                        <Text size="sm" fw={500}>
                                          {`${
                                            slugData?.currency
                                          } $${item.chargePerSquareUnit.basePrice.toFixed(
                                            2
                                          )}`}
                                        </Text>
                                      </Group>
                                    )}
                                  <Group justify="space-between">
                                    <Text size="sm" c="dimmed">
                                      {item.chargePerSquareUnit?.basePrice !=
                                      null
                                        ? "Est. total:"
                                        : "Price:"}
                                    </Text>
                                    <Text size="sm" fw={500}>
                                      {item.specimen.species === "Other"
                                        ? "POA (Price on Application)"
                                        : item.price
                                        ? `${
                                            slugData?.currency
                                          } $${item.price.toFixed(2)}`
                                        : "Calculating..."}
                                    </Text>
                                  </Group>
                                  {item.chargePerSquareUnit?.basePrice !=
                                    null && (
                                    <Text size="xs" c="dimmed">
                                      Final price &amp; size confirmed on
                                      intake.
                                    </Text>
                                  )}
                                </Stack>
                              )}
                          </Stack>
                        </Paper>
                      );
                    })}

                  <Button
                    fullWidth
                    size="md"
                    leftSection={<IconPlus size={16} />}
                    onClick={addMount}
                    variant="light"
                  >
                    {isPeak ? "Add Additional Mount" : "Add Mount"}
                  </Button>

                  {serviceOptions.length > 0 && (
                    <>
                      <Group justify="space-between">
                        <Stack gap={0}>
                          <Title order={4} mt="xs">
                            Additional Services
                          </Title>
                          <Text c="dimmed" size="sm">
                            Additional services you&apos;d like to include
                          </Text>
                        </Stack>
                        <Button
                          size="sm"
                          leftSection={<IconPlus size={16} />}
                          onClick={addService}
                          variant="light"
                        >
                          Add Service
                        </Button>
                      </Group>

                      {form.values.lineItems
                        .filter(isServiceLineItem)
                        .map((item) => {
                          const realIndex = form.values.lineItems.indexOf(item);
                          const serviceItems =
                            form.values.lineItems.filter(isServiceLineItem);
                          const serviceIndex = serviceItems.indexOf(item);
                          return (
                            <Paper key={item.uid} p={{ base: "sm", sm: "md" }} withBorder>
                              <Stack gap="sm">
                                <Group justify="space-between">
                                  <Text size="sm" fw={500}>
                                    Service #{serviceIndex + 1}
                                  </Text>
                                  <ActionIcon
                                    color="red"
                                    onClick={() => removeService(realIndex)}
                                  >
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Group>

                                <Select
                                  label="Service"
                                  searchable
                                  size="md"
                                  data={serviceOptions}
                                  value={item.service}
                                  onBlur={(e) =>
                                    commitTypedOption(
                                      `lineItems.${realIndex}.service`,
                                      findOptionValueByText(
                                        serviceOptions,
                                        e.currentTarget.value
                                      )
                                    )
                                  }
                                  onChange={(value) => {
                                    form.setFieldValue(
                                      `lineItems.${realIndex}.service`,
                                      value
                                    );
                                    // Price will be auto-updated by useEffect
                                  }}
                                />

                                <Textarea
                                  label="Notes"
                                  placeholder="Any special requests for this service..."
                                  size="md"
                                  value={item.notes}
                                  onChange={(e) =>
                                    form.setFieldValue(
                                      `lineItems.${realIndex}.notes`,
                                      e.target.value
                                    )
                                  }
                                />

                                {!slugData?.hidePricing && item.service && (
                                  <Group justify="apart">
                                    <Text size="sm" c="dimmed">
                                      Price:
                                    </Text>
                                    <Text size="sm" fw={500}>
                                      {item.price
                                        ? `${
                                            slugData?.currency
                                          } $${item.price.toFixed(2)}`
                                        : "Calculating..."}
                                    </Text>
                                  </Group>
                                )}
                              </Stack>
                            </Paper>
                          );
                        })}
                    </>
                  )}

                  {slugData?.wrdCollection && (
                    <>
                      <Divider />
                      <Title order={4}>{complianceLabels.formTitle}</Title>
                      {isMaineRecord && (
                        <Text size="sm" c="dimmed">
                          Maine requires taxidermists to record the license,
                          seal and MOSES details for every animal received.
                        </Text>
                      )}
                      {showHunterOption && (
                        <Stack gap="sm" mb="sm">
                          <SegmentedControl
                            fullWidth
                            size="md"
                            value={hunterIsBooker ? "me" : "other"}
                            onChange={(v) =>
                              form.setFieldValue(
                                "extraData.isHunter",
                                v === "me"
                              )
                            }
                            data={[
                              { label: "I am the hunter", value: "me" },
                              {
                                label: "Booking for someone else",
                                value: "other",
                              },
                            ]}
                          />
                          {!hunterIsBooker && (
                            <Paper withBorder radius="md" p="md">
                              <Stack gap="sm">
                                <Group
                                  justify="space-between"
                                  align="center"
                                  wrap="wrap"
                                >
                                  <Stack gap={0}>
                                    <Text fw={600}>Hunter</Text>
                                    <Text size="xs" c="dimmed">
                                      Named on the Wildlife Resource Document
                                    </Text>
                                  </Stack>
                                  <Switch
                                    label="Same address as mine"
                                    {...form.getInputProps(
                                      "extraData.hunterSameAddress",
                                      { type: "checkbox" }
                                    )}
                                  />
                                </Group>
                                <Grid>
                                  <Grid.Col span={{ base: 12, md: 4 }}>
                                    <TextInput
                                      required
                                      label="First Name"
                                      placeholder="First name"
                                      size="md"
                                      {...form.getInputProps(
                                        "extraData.hunter.firstName"
                                      )}
                                    />
                                  </Grid.Col>
                                  <Grid.Col span={{ base: 12, md: 4 }}>
                                    <TextInput
                                      required
                                      label="Last Name"
                                      placeholder="Last name"
                                      size="md"
                                      {...form.getInputProps(
                                        "extraData.hunter.lastName"
                                      )}
                                    />
                                  </Grid.Col>
                                  <Grid.Col span={{ base: 12, md: 4 }}>
                                    <TextInput
                                      label="Phone"
                                      placeholder="Optional"
                                      size="md"
                                      {...form.getInputProps(
                                        "extraData.hunter.phone"
                                      )}
                                    />
                                  </Grid.Col>
                                  {!hunterSameAddress && (
                                    <>
                                      <Grid.Col span={12}>
                                        <TextInput
                                          required
                                          label="Street Address"
                                          placeholder="Street address"
                                          size="md"
                                          {...form.getInputProps(
                                            "extraData.hunter.address.streetAddress"
                                          )}
                                        />
                                      </Grid.Col>
                                      <Grid.Col span={{ base: 12, md: 4 }}>
                                        <TextInput
                                          required
                                          label="City"
                                          placeholder="City"
                                          size="md"
                                          {...form.getInputProps(
                                            "extraData.hunter.address.city"
                                          )}
                                        />
                                      </Grid.Col>
                                      <Grid.Col span={{ base: 12, md: 4 }}>
                                        <Select
                                          required
                                          label="State"
                                          placeholder="State"
                                          size="md"
                                          searchable
                                          data={usStatesOptions}
                                          {...form.getInputProps(
                                            "extraData.hunter.address.state"
                                          )}
                                          onBlur={(e) =>
                                            commitTypedOption(
                                              "extraData.hunter.address.state",
                                              findUsStateByText(
                                                usStatesOptions,
                                                e.currentTarget.value
                                              )
                                            )
                                          }
                                        />
                                      </Grid.Col>
                                      <Grid.Col span={{ base: 12, md: 4 }}>
                                        <TextInput
                                          required
                                          label="Zip Code"
                                          placeholder="Zip"
                                          size="md"
                                          {...form.getInputProps(
                                            "extraData.hunter.address.zipCode"
                                          )}
                                        />
                                      </Grid.Col>
                                    </>
                                  )}
                                </Grid>
                              </Stack>
                            </Paper>
                          )}
                        </Stack>
                      )}
                      <Grid>
                        {isMaineRecord && (
                          <Grid.Col span={{ base: 12 }}>
                            <TextInput
                              required
                              label="MOSES ID / Conservation Number"
                              placeholder="Enter MOSES ID"
                              size="md"
                              {...form.getInputProps("extraData.mosesId")}
                            />
                          </Grid.Col>
                        )}
                        {!isPeak && (
                          <Grid.Col span={{ base: 12 }}>
                            <TextInput
                              required={
                                complianceLabels.harvestLocationRequired
                              }
                              label={complianceLabels.harvestLocationLabel}
                              placeholder={
                                complianceDocType !== "wrd"
                                  ? "Town or area where the animal was taken"
                                  : "Enter ranch or outfitter name"
                              }
                              size="md"
                              {...form.getInputProps(
                                "extraData.ranchOutfitter"
                              )}
                            />
                          </Grid.Col>
                        )}

                        {!isPeak && (
                          <Grid.Col span={{ base: 12, md: 6 }}>
                            <TextInput
                              required={slugData?.uid === NamedUserUID.TOPPIN}
                              label="County"
                              placeholder="Enter county"
                              size="md"
                              {...form.getInputProps("extraData.county")}
                            />
                          </Grid.Col>
                        )}
                        <Grid.Col span={{ base: 12, md: isPeak ? 12 : 6 }}>
                          <Select
                            required
                            label={
                              isMaineRecord
                                ? "State / Province of Origin"
                                : "State"
                            }
                            placeholder="Select state"
                            size="md"
                            searchable
                            data={usStatesOptions}
                            {...form.getInputProps("extraData.state")}
                            onBlur={(e) =>
                              commitTypedOption(
                                "extraData.state",
                                findUsStateByText(
                                  usStatesOptions,
                                  e.currentTarget.value
                                )
                              )
                            }
                          />
                        </Grid.Col>
                      </Grid>

                      <Grid>
                        <Grid.Col span={{ base: 12, md: licenseColSpan }}>
                          <Select
                            required
                            label="License Type"
                            placeholder="Select license type"
                            size="md"
                            data={
                              huntingLicenseOnly
                                ? [{ value: "hunting", label: "Hunting License" }]
                                : complianceLabels.licenseTypeOptions
                            }
                            disabled={huntingLicenseOnly}
                            {...form.getInputProps("extraData.licenseType")}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: licenseColSpan }}>
                          <TextInput
                            required
                            label={
                              huntingLicenseOnly
                                ? "Hunting License Number"
                                : complianceLabels.licenseNumberLabel ||
                                  "License Number"
                            }
                            placeholder={
                              huntingLicenseOnly
                                ? "Enter hunting license number"
                                : complianceLabels.licenseNumberLabel
                                ? "Enter number"
                                : "Enter license number"
                            }
                            size="md"
                            {...form.getInputProps("extraData.licenseNumber")}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: licenseColSpan }}>
                          <Select
                            required
                            label="License State"
                            placeholder="Select state"
                            size="md"
                            searchable
                            data={usStatesOptions}
                            {...form.getInputProps("extraData.licenseState")}
                            onBlur={(e) =>
                              commitTypedOption(
                                "extraData.licenseState",
                                findUsStateByText(
                                  usStatesOptions,
                                  e.currentTarget.value
                                )
                              )
                            }
                          />
                        </Grid.Col>
                      </Grid>

                      <Stack gap="xs">
                        <Stack gap={0}>
                          <InputLabel size="lg" required>
                            Signature
                          </InputLabel>
                          <Text size="sm" c="dimmed">
                            By signing below, you certify that the information
                            provided is correct and accurate
                          </Text>
                        </Stack>
                        <Paper
                          withBorder
                          style={{
                            display: "inline-block",
                            width: "100%",
                            borderColor: form.errors["extraData.signature"]
                              ? "red"
                              : undefined,
                          }}
                        >
                          {/* Height in CSS, not the height attribute: the
                              library scales the canvas by devicePixelRatio,
                              which shrank a fixed 400 to ~130px on phones */}
                          <SignatureCanvas
                            ref={signatureRef}
                            canvasProps={{
                              className: "signature-canvas",
                              style: {
                                border: "none",
                                display: "block",
                                width: "100%",
                                height: "clamp(220px, 35vh, 320px)",
                                borderRadius: "4px",
                                touchAction: "none",
                              },
                            }}
                            backgroundColor="rgba(255,255,255,1)"
                            penColor="#000000"
                            minWidth={0.5}
                            maxWidth={2.5}
                            velocityFilterWeight={0.7}
                            minDistance={0.5}
                            throttle={0}
                            clearOnResize={false}
                            onEnd={() => {
                              if (
                                signatureRef.current &&
                                !signatureRef.current.isEmpty()
                              ) {
                                const signatureData =
                                  signatureRef.current.toDataURL("image/png");
                                form.setFieldValue(
                                  "extraData.signature",
                                  signatureData
                                );
                                form.clearFieldError("extraData.signature");
                                setHasSignature(true);
                              }
                            }}
                          />
                        </Paper>
                        {form.errors["extraData.signature"] && (
                          <InputError size="md">
                            {form.errors["extraData.signature"]}
                          </InputError>
                        )}
                        {hasSignature && (
                          <Button
                            size="md"
                            variant="outline"
                            leftSection={<IconEraser size={16} />}
                            onClick={() => {
                              signatureRef.current?.clear();
                              form.setFieldValue("extraData.signature", "");
                              setHasSignature(false);
                            }}
                            style={{ width: "fit-content" }}
                          >
                            Clear Signature
                          </Button>
                        )}
                      </Stack>
                      <Divider />
                    </>
                  )}

                  {!slugData?.wrdCollection &&
                    slugData?.uid === NamedUserUID.SKINS_AND_SCALES && (
                      <>
                        <Divider />
                        <TextInput
                          required
                          label="Ranch/Outfitter"
                          placeholder="Enter ranch or outfitter name"
                          size="md"
                          {...form.getInputProps("extraData.ranchOutfitter")}
                        />
                      </>
                    )}

                  {/* Total Price Display */}
                  {!slugData?.hidePricing &&
                    form.values.lineItems.some(
                      (item) =>
                        (isSpecimenLineItem(item) &&
                          item.specimen.species &&
                          item.specimen.mountType) ||
                        (isServiceLineItem(item) && item.service)
                    ) && (
                      <>
                        <Paper
                          p="md"
                          withBorder
                          style={{ backgroundColor: primaryColor }}
                        >
                          <Stack gap="xs">
                            <Group gap="xs">
                              <Text size="lg" fw={500} c={secondaryColor}>
                                Estimated Total:
                              </Text>
                              <Text size="xl" fw={700} c={secondaryColor}>
                                {selectedCurrency || slugData?.currency} $
                                {form.values.grandTotal?.toFixed(2) || "0.00"}
                                {form.values.includeTax && (
                                  <Text ml="xs" size="sm" fw={500} span>
                                    (Tax {form.values.taxRate}%)
                                  </Text>
                                )}
                              </Text>
                            </Group>
                          </Stack>
                        </Paper>
                      </>
                    )}

                  {/* Always shown when set, regardless of hidden pricing. */}
                  {!!slugData?.disclaimerText?.trim() && (
                    <Alert
                      icon={<IconAlertCircle />}
                      color="yellow"
                      title="Important Disclaimer"
                    >
                      <Text size="sm">{slugData.disclaimerText}</Text>
                    </Alert>
                  )}

                  {error && (
                    <Alert icon={<IconAlertCircle />} color="red">
                      {error}
                    </Alert>
                  )}

                  <Button
                    disabled={Object.keys(form.errors).length > 0}
                    type="submit"
                    size="lg"
                    fullWidth
                    loading={submitting}
                    bg={
                      Object.keys(form.errors).length > 0
                        ? "#f0f0f0"
                        : primaryColor
                    }
                    c={
                      Object.keys(form.errors).length > 0
                        ? "#8c8c8c"
                        : secondaryColor
                    }
                    loaderProps={{
                      ...(primaryColor === "#ffffff"
                        ? { color: secondaryColor || "inherit" }
                        : {}),
                    }}
                    style={{
                      border: `${
                        primaryColor === "#ffffff"
                          ? "1px solid " + secondaryColor
                          : "inherit"
                      }`,
                    }}
                  >
                    Submit Booking Request
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Paper>
        </Container>
      </ScrollArea>
      {/* <Box h="100dvh">
        <WRDPdf
          job={{
            uid: "ee5c7881c30e4326b2de",
            jobId: 1234567890,
            staffQuery: [],
            actionsQuery: [],
            history: [],
            payments: [],
            staff: [],
            actions: [],
            client: {
              uid: "",
              firstName: "Luke",
              lastName: "Markham",
              fullName: "",
              currency: {
                code: "USD",
                symbol: "$",
              },
              contactDetails: {
                phone: "+12102321332",
                email: "contact@mountmonitor.com",
              },
              address: {
                streetAddress: "123 Main Street",
                city: "Horton",
                county: "Bexar",
                state: "Texas",
                zipCode: "23123",
                country: "United States",
              },
              syncedToAccountingIntegration: false,
              autoStageEmails: null,
              autoStageSMS: null,
            },
            shipping: "Pickup",
            priority: null,
            tags: ["active"],
            notes: "",
            ids: [],
            files: [],
            lineItems: [
              {
                uid: "fe7f562f829449a596be",
                frontFacingId: "M1",
                quantity: 1,
                stage: null,
                habitat: null,
                estimatedCompletedAt: null,
                completedAt: null,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                tags: [],
                files: [],
                notes: "",
                price: 950,
                chargePerSquareUnit: null,
                deposit: 475,
                depositAmount: 50,
                depositType: "percentage",
                discount: null,
                discountAmount: null,
                discountType: "percentage",
                discountedPrice: null,
                specimen: {
                  species: "White-tailed Deer Buck",
                  mountType: "Shoulder",
                  mainPose: "Heavy Sneak",
                  subPose: null,
                  direction: null,
                  degrees: null,
                  harvestMethod: null,
                  harvestDate: Timestamp.now(),
                  location: null,
                  condition: null,
                  eyeColor: null,
                  measurements: null,
                  parts: [
                    {
                      uid: "bedbca35303f40049026",
                      jobUid: "",
                      lineItemUid: "",
                      jobId: 0,
                      name: "Whole",
                      locationCode: null,
                      species: null,
                      frontFacingId: "",
                      createdAt: {},
                      updatedAt: {},
                    },
                  ],
                },
              },
            ],
            subtotal: 950,
            totalDiscount: 0,
            subtotalAfterDiscounts: 950,
            taxRate: 6.75,
            includeTax: true,
            totalTax: 64.125,
            grandTotal: 1014.125,
            totalDeposit: 475,
            remainingBalance: 1014.125,
            paymentStatus: PaymentStatus.AwaitingDeposit,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            completedAt: null,
            syncedToAccountingIntegration: false,
            extraData: {
              ranchOutfitter: "ox ranch",
              county: "Ulvada",
              state: "Texas",
              licenseType: "hunting",
              licenseNumber: "213123",
              signature:
                "data:image/png;base64,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",
            },
          }}
          userUid={slugData?.uid}
        />
      </Box> */}
    </Stack>
  );
}
